// src/lib/http/bffClient.ts
// Tiny, shareable HTTP client for talking to the BFF from browser or SSR/Node.

export interface BffErrorBody { [k: string]: any }

export class BffError extends Error {
  status: number;
  body: BffErrorBody | string | undefined;
  constructor(message: string, status: number, body?: BffErrorBody | string) {
    super(message);
    this.name = 'BffError';
    this.status = status;
    this.body = body;
  }
}

/** Resolve the BFF origin in browser, Vite, CRA, or Node. */
function resolveOrigin(): string {
  const IS_BROWSER = typeof window !== 'undefined';
  const w: any = IS_BROWSER ? window : undefined;
  const ENV: any = (typeof globalThis !== 'undefined' && (globalThis as any).process && (globalThis as any).process.env) || {};

  const fromWindow =
    (IS_BROWSER && (w?.__BFF_ORIGIN__ ?? w?.BFF_ORIGIN)) || undefined;

  const fromCRA = ENV.REACT_APP_BFF || undefined;

  const fromNode = (!IS_BROWSER && (ENV.BFF_ORIGIN || ENV.REACT_APP_BFF || ENV.VITE_BFF_ORIGIN)) || undefined;

  // Dev fallback: if the SPA runs on 3000, assume BFF on 8080
  const devDefault =
    IS_BROWSER && window.location.port === '3000' ? 'http://localhost:8080' : undefined;

  return (
    fromWindow ||
    fromCRA ||
    fromNode ||
    devDefault ||
    (IS_BROWSER ? window.location.origin : 'http://localhost:8080')
  );
}

type AuthHeaderProvider = () => Record<string, string> | null | undefined;

export type ClientOptions = {
  origin?: string;               // e.g. http://localhost:8080
  basePath?: string;             // e.g. /bff  or  /bff/ttlock/api
  timeoutMs?: number;            // default 12_000
  defaultHeaders?: Record<string, string>;
  getAuthHeaders?: AuthHeaderProvider; // optional; cookie-first by default
  /**
   * Called when the client determines the user is unauthenticated (e.g. refresh fails).
   * Typical behavior: clear app state and redirect to /login.
   */
  onUnauthenticated?: () => void;
};

type Queryish = Record<string, string | number | boolean | undefined>;

function joinPath(a: string, b: string) {
  return `${a.replace(/\/+$/, '')}/${b.replace(/^\/+/, '')}`;
}

// Proper timeout helper that passes an AbortSignal into your fetch.
//
// `AbortController.abort()` without an explicit reason makes fetch reject
// with the cryptic browser-default `"signal is aborted without reason"`,
// which has leaked into user-facing error banners in the past. We pass an
// explicit `TimeoutError` DOMException so:
//   • `err.name === 'TimeoutError'` is checkable upstream;
//   • the message is human-readable when it does leak through;
//   • behaviour matches the modern `AbortSignal.timeout(ms)` contract
//     without dropping support for browsers we still target.
async function withTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  ms: number
): Promise<T> {
  if (!ms || ms <= 0) return fn(new AbortController().signal);
  const ctrl = new AbortController();
  const timer = setTimeout(
    () => ctrl.abort(new DOMException(`Request timed out after ${ms}ms`, 'TimeoutError')),
    ms,
  );
  try {
    return await fn(ctrl.signal);
  } finally {
    clearTimeout(timer);
  }
}

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.split('; ').find(c => c.startsWith(`${name}=`));
  return m ? decodeURIComponent(m.split('=').slice(1).join('=')) : null;
}

export function createBffClient(opts: ClientOptions = {}) {
  const ORIGIN   = opts.origin ?? resolveOrigin();
  const BASEPATH = opts.basePath ?? '/bff';
  const TIMEOUT  = opts.timeoutMs ?? 12_000;
  const DEF_HDRS = opts.defaultHeaders ?? {};
  let getAuth    = opts.getAuthHeaders ?? null;
  const onUnauthenticated = opts.onUnauthenticated;

  // Prevent auth refresh stampedes and infinite loops.
  let refreshInFlight: Promise<boolean> | null = null;
  let oidcBridgeInFlight: Promise<boolean> | null = null;
  let authKnownBad = false;
  let unauthNotified = false;

  // Cross-tab refresh coordination (best-effort).
  const TAB_ID = (() => {
    try {
      return (globalThis as any).crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    } catch {
      return `${Date.now()}-${Math.random()}`;
    }
  })();

  const REFRESH_LOCK_KEY = 'pacmaster:auth:refreshLock';
  const REFRESH_WAIT_MS = 8_000;
  const REFRESH_LOCK_TTL_MS = 12_000;

  type RefreshWireMsg =
    | { v: 1; t: 'refresh:start'; src: string; at: number }
    | { v: 1; t: 'refresh:done';  src: string; at: number; ok: boolean | null };

  let bc: BroadcastChannel | null = null;
  const refreshWaiters = new Set<(ok: boolean | null) => void>();

  function ensureRefreshChannel() {
    if (typeof window === 'undefined') return;
    if (typeof BroadcastChannel === 'undefined') return;
    if (bc) return;
    try {
      bc = new BroadcastChannel('pacmaster:auth');
      bc.onmessage = (ev: MessageEvent) => {
        const msg = ev?.data as RefreshWireMsg | undefined;
        if (!msg || msg.v !== 1) return;
        if (msg.src === TAB_ID) return;
        if (msg.t === 'refresh:done') {
          for (const w of Array.from(refreshWaiters)) {
            try { w(msg.ok); } catch { /* ignore */ }
          }
          refreshWaiters.clear();
        }
      };
    } catch {
      bc = null;
    }
  }

  function wirePost(msg: RefreshWireMsg) {
    ensureRefreshChannel();
    try { bc?.postMessage(msg); } catch { /* ignore */ }
  }

  function readLock(): { owner: string; exp: number } | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(REFRESH_LOCK_KEY);
      if (!raw) return null;
      const j = JSON.parse(raw);
      if (!j || typeof j !== 'object') return null;
      if (typeof j.owner !== 'string' || typeof j.exp !== 'number') return null;
      if (j.exp < Date.now()) return null;
      return { owner: j.owner, exp: j.exp };
    } catch {
      return null;
    }
  }

  function tryAcquireLock(): boolean {
    if (typeof localStorage === 'undefined') return true;
    const cur = readLock();
    if (cur && cur.owner !== TAB_ID) return false;
    const next = { owner: TAB_ID, exp: Date.now() + REFRESH_LOCK_TTL_MS };
    try {
      localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(next));
      // Verify best-effort (helps avoid some races)
      const ver = readLock();
      return !!ver && ver.owner === TAB_ID;
    } catch {
      return true;
    }
  }

  function releaseLock() {
    if (typeof localStorage === 'undefined') return;
    try {
      const cur = readLock();
      if (cur?.owner === TAB_ID) localStorage.removeItem(REFRESH_LOCK_KEY);
    } catch {
      // ignore
    }
  }

  async function waitForOtherTabRefresh(): Promise<boolean | null> {
    ensureRefreshChannel();
    // If there's no channel support, just don't wait.
    if (!bc) return null;

    const existing = readLock();
    if (!existing || existing.owner === TAB_ID) return null;

    return await new Promise<boolean | null>((resolve) => {
      const timer = setTimeout(() => {
        refreshWaiters.delete(onDone);
        resolve(null);
      }, REFRESH_WAIT_MS);

      const onDone = (ok: boolean | null) => {
        clearTimeout(timer);
        refreshWaiters.delete(onDone);
        resolve(ok);
      };

      refreshWaiters.add(onDone);
    });
  }

  function notifyUnauthenticatedOnce() {
    if (unauthNotified) return;
    unauthNotified = true;
    authKnownBad = true;
    try { onUnauthenticated?.(); } catch { /* ignore */ }
  }

  async function tryOidcBridgeOnce(): Promise<boolean> {
    if (oidcBridgeInFlight) return oidcBridgeInFlight;

    oidcBridgeInFlight = (async () => {
      const bridgeHeaders = new Headers({ accept: 'application/json' });
      const csrf = getCookie('csrf');
      if (csrf) bridgeHeaders.set('x-csrf', csrf);

      try {
        const bridgeRes = await fetch(makeUrl('/auth/oidc/session/bridge').toString(), {
          method: 'POST',
          headers: bridgeHeaders,
          credentials: 'include',
        });
        if (!bridgeRes.ok) return false;
        authKnownBad = false;
        unauthNotified = false;
        return true;
      } catch {
        return false;
      } finally {
        oidcBridgeInFlight = null;
      }
    })();

    return oidcBridgeInFlight;
  }

  async function tryRefreshOnce(): Promise<boolean> {
    if (authKnownBad) return false;
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
      // If another tab is refreshing, wait for it and reuse the result.
      const waited = await waitForOtherTabRefresh();
      if (waited === true) return true;
      if (waited === false) {
        notifyUnauthenticatedOnce();
        return false;
      }

      const isLeader = tryAcquireLock();
      if (!isLeader) {
        // Lost a race: wait one more time.
        const waited2 = await waitForOtherTabRefresh();
        if (waited2 === true) return true;
        if (waited2 === false) {
          notifyUnauthenticatedOnce();
          return false;
        }
      }

      try {
        wirePost({ v: 1, t: 'refresh:start', src: TAB_ID, at: Date.now() });
        const refreshUrl = makeUrl('/auth/refresh');
        const refreshHeaders = new Headers();
        const csrf = getCookie('csrf');
        if (csrf) refreshHeaders.set('x-csrf', csrf);
        refreshHeaders.set('accept', 'application/json');

        const refreshRes = await fetch(refreshUrl.toString(), {
          method: 'POST',
          credentials: 'include',
          headers: refreshHeaders,
        });

        if (!refreshRes.ok) {
          // Only 401/403 means the session/refresh token is invalid/expired.
          // Other statuses can be transient (gateway hiccup, race with another tab, etc.)
          if (refreshRes.status === 401 || refreshRes.status === 403) {
            notifyUnauthenticatedOnce();
            wirePost({ v: 1, t: 'refresh:done', src: TAB_ID, at: Date.now(), ok: false });
          }
          if (refreshRes.status !== 401 && refreshRes.status !== 403) {
            wirePost({ v: 1, t: 'refresh:done', src: TAB_ID, at: Date.now(), ok: null });
          }
          return false;
        }

        wirePost({ v: 1, t: 'refresh:done', src: TAB_ID, at: Date.now(), ok: true });
        return true;
      } catch {
        // Network errors: do not mark auth as bad; just report refresh as failed.
        wirePost({ v: 1, t: 'refresh:done', src: TAB_ID, at: Date.now(), ok: null });
        return false;
      } finally {
        refreshInFlight = null;
        releaseLock();
      }
    })();

    return refreshInFlight;
  }

  function makeUrl(path: string, qs?: Queryish) {
    const url = new URL(joinPath(ORIGIN, joinPath(BASEPATH, path)));
    if (qs) for (const [k, v] of Object.entries(qs)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
    return url;
  }

  async function request<T>(
    path: string,
    init?: (RequestInit & { qs?: Queryish; autoRefresh?: boolean; timeoutMs?: number })
  ): Promise<T> {
    const url = makeUrl(path, init?.qs);
    const method = (init?.method || 'GET').toUpperCase();

    const isLoginAttempt = url.pathname.endsWith('/auth/login');
    const isRefreshRoute = url.pathname.endsWith('/auth/refresh');
    const isOidcBridgeRoute = url.pathname.endsWith('/auth/oidc/session/bridge');
    const isAuthRefreshableRoute = isLoginAttempt || isRefreshRoute;

    // Build headers with the real Headers class to satisfy TS/Fetch
    const headers = new Headers({ ...DEF_HDRS, ...(init?.headers as Record<string, string> | undefined) });

    // Optional auth headers (mostly unnecessary when using cookies; still supported)
    if (getAuth) {
      const extra = getAuth();
      if (extra) for (const [k, v] of Object.entries(extra)) headers.set(k, v);
    }

    // Set Accept if absent
    if (!headers.has('accept')) headers.set('accept', 'application/json');

    // Content-Type convenience (but never for FormData)
    const hasBody   = init && 'body' in init && init.body != null;
    const isForm    = typeof FormData !== 'undefined' && (init?.body instanceof FormData);
    if (hasBody && !isForm && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }

    // CSRF on unsafe methods (if your BFF sets a csrf cookie)
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const csrf = getCookie('csrf');
      if (csrf && !headers.has('x-csrf')) headers.set('x-csrf', csrf);
    }

    const doFetch = async (signal: AbortSignal) => {
      let res: Response;
      try {
        res = await fetch(url.toString(), {
          ...init,
          method,
          headers,
          signal,
          credentials: 'include', // send cookies to BFF
        });
      } catch (err) {
        // Network/timeout errors are not a confirmed auth loss.
        // Let callers decide how to surface connectivity problems.
        throw err;
      }

      // Handle 401 → try one silent refresh (unless this *is* the refresh)
      const auto = init?.autoRefresh ?? true;

      // If the gateway is down (or upstream timed out), treat it as a disconnect,
      // but do NOT force-logout the user.
      // Callers can surface availability errors without destroying session state.

      if (res.status === 401 && auto && !isAuthRefreshableRoute && !isOidcBridgeRoute && !authKnownBad) {
        const bridged = await tryOidcBridgeOnce();
        if (bridged) {
          const retryAfterBridge = await fetch(url.toString(), {
            ...init,
            method,
            headers,
            signal,
            credentials: 'include',
          });
          if (retryAfterBridge.status !== 401) {
            return retryAfterBridge;
          }
          res = retryAfterBridge;
        }

        const refreshed = await tryRefreshOnce();
        if (refreshed) {
          const retry = await fetch(url.toString(), {
            ...init,
            method,
            headers,
            signal,
            credentials: 'include',
          });
          // The refresh PROVED the session is alive (the BFF rotated our
          // tokens). A 401/403 that survives a successful refresh is therefore
          // strictly per-resource — the caller is authenticated but lacks the
          // permission THIS endpoint requires (e.g. a TableOps user hitting
          // /usage/summary, gated behind `workflow.runs.view`). It is NOT
          // session death, so we must NOT tear down the session. Surface it as
          // a normal per-feature error and let the global WWW-Authenticate
          // interceptor remain the sole authority on real session loss.
          return retry;
        }
        // If refresh didn't work, keep the original 401.
      }

      // If the server explicitly says 401, the session is not usable.
      // Do NOT trigger on login attempts (bad password should not redirect).
      if (res.status === 401 && !isLoginAttempt) {
        // Only treat as a confirmed auth drop if we already know the session is bad
        // (e.g. refresh returned 401/403 or retry still failed).
        if (authKnownBad) notifyUnauthenticatedOnce();
      }

      return res;
    };

  const effTimeout = init?.timeoutMs != null ? init.timeoutMs : TIMEOUT;
  const res = await withTimeout(doFetch, effTimeout);
    const text = await res.text().catch(() => '');

    // Try to parse JSON when possible
    const tryJson = () => {
      try { return text ? JSON.parse(text) : undefined; }
      catch { return undefined; }
    };

    if (!res.ok) {
      const body = tryJson() ?? text;
      // Prefer a useful message from body if present
      const extractMsg = (v: unknown): string | undefined => {
        if (typeof v === 'string' && v) return v;
        if (typeof v === 'object' && v !== null) {
          const o = v as Record<string, unknown>;
          // Prefer a human-facing message/detail before short error codes.
          return extractMsg(o.message) ?? extractMsg(o.detail) ?? extractMsg(o.error);
        }
        return undefined;
      };
      const extracted = (typeof body === 'object' && body) ? extractMsg(body) : undefined;
      const msg = extracted
        ? `${extracted} (${res.status} ${res.statusText})`
        : `${method} ${url.pathname} → ${res.status} ${res.statusText}`;
      throw new BffError(msg, res.status, body);
    }

    const json = (tryJson() ?? ({} as any)) as T;
    // Attach HTTP status so dual-mode hooks can detect 202 Accepted
    if (typeof json === 'object' && json !== null) {
      (json as any).__httpStatus = res.status;
    }
    return json;
  }

  const get   = <T>(p: string, qs?: Queryish) => request<T>(p, { method: 'GET', qs });
  const del   = <T>(p: string) => request<T>(p, { method: 'DELETE' });
  const post  = <T, B = unknown>(p: string, body?: B, opts?: { timeoutMs?: number }) =>
    request<T>(p, {
      method: 'POST',
      timeoutMs: opts?.timeoutMs,
      body: (() => {
        if (!body) return undefined as any;
        if ((body as any) instanceof FormData) return body as any;
        // Ensure we never send a JSON "undefined" or accidentally drop everything.
        const json = JSON.stringify(body);
        if (json === undefined) {
          throw new Error('BFF client: request body is not JSON-serializable.');
        }
        return json;
      })()
    });
  const put  = <T, B = unknown>(p: string, body?: B, opts?: { timeoutMs?: number }) =>
    request<T>(p, {
      method: 'PUT',
      timeoutMs: opts?.timeoutMs,
      body: (() => {
        if (!body) return undefined as any;
        if ((body as any) instanceof FormData) return body as any;
        const json = JSON.stringify(body);
        if (json === undefined) {
          throw new Error('BFF client: request body is not JSON-serializable.');
        }
        return json;
      })()
    });
  const patch = <T, B = unknown>(p: string, body?: B, opts?: { timeoutMs?: number }) =>
    request<T>(p, {
      method: 'PATCH',
      timeoutMs: opts?.timeoutMs,
      body: (() => {
        if (!body) return undefined as any;
        if ((body as any) instanceof FormData) return body as any;
        const json = JSON.stringify(body);
        if (json === undefined) {
          throw new Error('BFF client: request body is not JSON-serializable.');
        }
        return json;
      })()
    });

  return {
    origin  : ORIGIN,
    basePath: BASEPATH,
    setAuthProvider(fn: AuthHeaderProvider) { getAuth = fn; },
    /**
     * Read the current auth/override headers (e.g. `X-Tenant-Override`).
     *
     * Exposed for streaming primitives that bypass the regular `request()`
     * pipeline (browser `EventSource`, raw `fetch` SSE consumers) and need
     * to attach the same headers/query params as a normal BFF call so the
     * superadmin tenant impersonation context is preserved end-to-end.
     */
    getAuthHeaders(): Record<string, string> {
      const extra = getAuth ? getAuth() : null;
      return extra && typeof extra === 'object' ? { ...extra } : {};
    },
    request, get, del, post, put, patch,
  };
}
